// Slack-style link-edit popover. Renders an absolutely-positioned
// floating card with a single URL input and Save / Remove buttons.
// Anchored to the link the user clicked (anchorRect, in viewport
// coordinates).
//
// The composer owns the visible/hidden state — this component is
// purely presentational. On save it calls `onSave(url)` and on
// remove it calls `onRemove()`; the composer routes both back into
// the editor through `editor.setLink(url)` / `editor.unwrapLink()`.

import React, { useEffect, useRef, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import { useColorScheme } from '../../foundations/colorScheme';
import { ExternalLink, Trash2, Type } from 'lucide-react-native';
import { createTheme } from '../../design-system/theme';

export interface LinkEditPopoverProps {
  readonly visible: boolean;
  readonly initialUrl: string;
  /** Current link text; shown in the editable Text field. */
  readonly initialLabel?: string;
  /** Viewport-coordinate rect of the link being edited (the anchor). */
  readonly anchorRect: { x: number; y: number; width: number; height: number } | null;
  readonly onSave: (url: string, label: string) => void;
  readonly onRemove: () => void;
  readonly onDismiss: () => void;
  readonly testID?: string;
}

const POPOVER_WIDTH = 320;
const POPOVER_GAP = 8;

export const LinkEditPopover = React.memo<LinkEditPopoverProps>(
  ({ visible, initialUrl, initialLabel = '', anchorRect, onSave, onRemove, onDismiss, testID }) => {
    const scheme = useColorScheme();
    const colorScheme: 'light' | 'dark' = scheme === 'dark' ? 'dark' : 'light';
    const theme = createTheme(colorScheme);
    const [url, setUrl] = useState(initialUrl);
    const [label, setLabel] = useState(initialLabel);
    const inputRef = useRef<TextInput | null>(null);

    // Sync the inputs with the new initial values whenever the popover
    // (re-)opens — otherwise reopening for a different link would
    // show the stale buffer.
    useEffect(() => {
      if (visible) {
        setUrl(initialUrl);
        setLabel(initialLabel);
        // Defer to next tick so the popover is mounted before focus.
        const t = setTimeout(() => inputRef.current?.focus(), 30);
        return () => clearTimeout(t);
      }
      return undefined;
    }, [visible, initialUrl, initialLabel]);

    const commit = (): void => onSave(url.trim(), label.trim());

    // Measured card height so the flip decision + upward pin use the
    // real height instead of a guess — that's what makes the popover
    // hug the link tightly (like the mention picker) rather than
    // floating with a stale offset.
    const [measuredHeight, setMeasuredHeight] = useState(0);

    if (!visible || anchorRect === null) return null;

    // Position the popover just BELOW the link by default, flipping to
    // just ABOVE when there isn't room below. Both placements keep a
    // small `POPOVER_GAP` to the link so it reads as attached to it.
    let top = anchorRect.y + anchorRect.height + POPOVER_GAP;
    let left = anchorRect.x;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const h = measuredHeight > 0 ? measuredHeight : 160;
      // Flip up (pin the card's BOTTOM just above the link) when the
      // card would overflow the viewport bottom below the link.
      if (top + h > viewportHeight - 12) {
        top = anchorRect.y - POPOVER_GAP - h;
      }
      // Never let the top escape the viewport (very tall card / tiny
      // viewport): clamp into [12, vh - h - 12].
      const maxTop = Math.max(12, viewportHeight - h - 12);
      top = Math.min(Math.max(top, 12), maxTop);
      // Clamp horizontally so the card stays on-screen, hugging the
      // link's left edge otherwise.
      if (left + POPOVER_WIDTH > viewportWidth - 12) {
        left = viewportWidth - POPOVER_WIDTH - 12;
      }
      if (left < 12) left = 12;
    }

    const bg =
      colorScheme === 'dark' ? '#1A1C22' : theme.colors.surface.primary;
    const border =
      colorScheme === 'dark' ? '#2F333B' : theme.colors.border.secondary;
    const inputBg =
      colorScheme === 'dark' ? '#222429' : theme.colors.surface.secondary;
    const textColor = theme.colors.text.primary;
    const subtleColor = theme.colors.text.secondary;
    const indigo = theme.colors.accent.indigo;

    // RN-Web 0.20+ deprecated `shadow*` style props in favor of
    // `boxShadow`. Native (iOS / macOS) still wants `shadow*` so
    // we branch — keeping the StyleSheet entry without shadows and
    // merging the platform-correct elevation here.
    const elevation =
      Platform.OS === 'web'
        ? ({ boxShadow: '0px 4px 12px rgba(0,0,0,0.18)' } as unknown as Record<
            string,
            unknown
          >)
        : ({
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.18,
            shadowRadius: 12,
          } as Record<string, unknown>);

    return (
      <View
        // `position: fixed` keeps the popover anchored to viewport
        // coordinates (which is what `anchorRect` carries). RN's
        // type system doesn't enumerate `fixed`; the cast through
        // `absolute` is web-only and harmless on native (which
        // never mounts this popover).
        onLayout={(e) => {
          const h = Math.round(e.nativeEvent.layout.height);
          if (h > 0 && h !== measuredHeight) setMeasuredHeight(h);
        }}
        style={[
          styles.root,
          {
            position:
              Platform.OS === 'web'
                ? ('fixed' as unknown as 'absolute')
                : 'absolute',
            top,
            left,
            backgroundColor: bg,
            borderColor: border,
            borderWidth: StyleSheet.hairlineWidth,
          },
          elevation as object,
        ]}
        testID={testID}
        accessibilityRole="menu"
        accessibilityLabel="Edit link"
      >
        {/* Text (label) field — Slack's link dialog Text input. */}
        <View style={styles.row}>
          <Type size={16} color={subtleColor} strokeWidth={2} />
          <TextInput
            ref={inputRef}
            value={label}
            onChangeText={setLabel}
            autoCapitalize="sentences"
            autoCorrect={true}
            placeholder="Text to display"
            placeholderTextColor={subtleColor}
            onSubmitEditing={commit}
            returnKeyType="next"
            style={[
              styles.input,
              { color: textColor, backgroundColor: inputBg },
            ]}
            testID="link-edit-label-input"
          />
        </View>
        <View style={styles.row}>
          <ExternalLink size={16} color={subtleColor} strokeWidth={2} />
          <TextInput
            value={url}
            onChangeText={setUrl}
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            keyboardType="url"
            placeholder="https://example.com"
            placeholderTextColor={subtleColor}
            onSubmitEditing={commit}
            style={[
              styles.input,
              { color: textColor, backgroundColor: inputBg },
            ]}
            testID="link-edit-input"
          />
        </View>
        <View style={styles.actions}>
          <Pressable
            onPress={() => onRemove()}
            accessibilityRole="button"
            accessibilityLabel="Remove link"
            style={({ pressed }) => [
              styles.removeButton,
              pressed ? { opacity: 0.7 } : null,
            ]}
            testID="link-edit-remove"
          >
            <Trash2 size={14} color={subtleColor} strokeWidth={2} />
            <Text style={[styles.removeLabel, { color: subtleColor }]}>
              Remove
            </Text>
          </Pressable>
          <View style={styles.actionsRight}>
            <Pressable
              onPress={() => onDismiss()}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              style={({ pressed }) => [
                styles.secondaryButton,
                pressed ? { opacity: 0.7 } : null,
              ]}
              testID="link-edit-cancel"
            >
              <Text style={[styles.secondaryLabel, { color: textColor }]}>
                Cancel
              </Text>
            </Pressable>
            <Pressable
              onPress={commit}
              accessibilityRole="button"
              accessibilityLabel="Save link"
              style={({ pressed }) => [
                styles.primaryButton,
                { backgroundColor: indigo },
                pressed ? { opacity: 0.85 } : null,
              ]}
              testID="link-edit-save"
            >
              <Text style={styles.primaryLabel}>Save</Text>
            </Pressable>
          </View>
        </View>
      </View>
    );
  },
);

LinkEditPopover.displayName = 'LinkEditPopover';

const styles = StyleSheet.create({
  root: {
    // `position` is overridden inline to `fixed` on web (viewport
    // coords) and `absolute` on native — `position: 'absolute'`
    // here just keeps the type system happy with the StyleSheet.
    // Shadow / elevation is also overridden inline so we can pick
    // the non-deprecated `boxShadow` shape on web.
    position: 'absolute',
    width: POPOVER_WIDTH,
    borderRadius: 10,
    padding: 10,
    gap: 8,
    zIndex: 1000,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  input: {
    flex: 1,
    fontSize: 14,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
    minWidth: 0,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
  },
  actionsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  removeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 6,
    borderRadius: 6,
  },
  removeLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  secondaryButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 6,
  },
  secondaryLabel: {
    fontSize: 13,
    fontWeight: '500',
  },
  primaryButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  primaryLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
